import React, { useState } from 'react';
import { Send, CheckCircle, MessageCircle, Mail, Phone } from 'lucide-react';
import { api } from '../services/api';
import { useModeration, MODERATION_CONTEXTS, MODERATION_ACTIONS } from '../hooks/useModeration';
import useSiteConfig from '../hooks/useSiteConfig';


export default function FaleConosco() {
  const { config } = useSiteConfig();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    assunto: '',
    mensagem: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Moderation
  const { checkMultipleFields, hasBlockingIssues } = useModeration();

  const formatWhatsapp = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check moderation for text fields (using CONTACT_FORM context which is more lenient)
      const fieldsToModerate = [
        { field: 'mensagem', text: formData.mensagem, context: MODERATION_CONTEXTS.CONTACT_FORM },
      ];
      
      const results = await checkMultipleFields(fieldsToModerate);
      
      // Check if any content was blocked
      if (hasBlockingIssues(results)) {
        setError('Sua mensagem contém conteúdo que não pode ser enviado. Por favor, revise o texto.');
        setLoading(false);
        return;
      }

      await api.post('/contato', { ...formData, categoria: formData.assunto });
      setSubmitted(true);
      setFormData({
        nome: '',
        email: '',
        whatsapp: '',
        assunto: '',
        mensagem: ''
      });
    } catch (err) {
      setError('Erro ao enviar mensagem. Tente novamente.');
      console.error('Erro ao enviar contato:', err);
    } finally {
      setLoading(false);
    }
  };

  const assuntos = [
    'Dúvidas sobre cadastro',
    'Problemas técnicos',
    'Parcerias',
    'Sugestões',
    'Reclamações',
    'Outros'
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--background)]">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
              Mensagem Enviada!
            </h1>
            <p className="text-[var(--text-secondary)] mb-6">
              Obrigado por entrar em contato. Nossa equipe responderá em breve.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="bg-[var(--primary)] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[var(--primary)]/90 transition-all"
            >
              Enviar outra mensagem
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-[#7B2FA8] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
            Fale Conosco
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Tem dúvidas, sugestões ou quer saber mais sobre o Empreende Comunidade? 
            Entre em contato conosco!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="font-bold text-lg mb-4" style={{ fontFamily: 'Manrope' }}>
                Outras formas de contato
              </h2>
              
              {/* Dados de contato vêm de Admin > Configurações do Site (por município) */}
              <div className="space-y-4">
                {config.telefone_contato && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                      <MessageCircle className="w-5 h-5 text-[var(--primary)]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">WhatsApp</p>
                      <a
                        href={`https://wa.me/55${config.telefone_contato.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--primary)] text-sm hover:underline"
                        data-testid="contact-info-whatsapp"
                      >
                        {config.telefone_contato}
                      </a>
                    </div>
                  </div>
                )}

                {config.telefone_contato && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                      <Phone className="w-5 h-5 text-[var(--primary)]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Telefone</p>
                      <a
                        href={`tel:${config.telefone_contato.replace(/\D/g, '')}`}
                        className="text-[var(--primary)] text-sm hover:underline"
                        data-testid="contact-info-telefone"
                      >
                        {config.telefone_contato}
                      </a>
                    </div>
                  </div>
                )}

                {config.email_contato && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
                      <Mail className="w-5 h-5 text-[var(--primary)]" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">E-mail</p>
                      <a
                        href={`mailto:${config.email_contato}`}
                        className="text-[var(--primary)] text-sm hover:underline break-all"
                        data-testid="contact-info-email"
                      >
                        {config.email_contato}
                      </a>
                    </div>
                  </div>
                )}

                {!config.telefone_contato && !config.email_contato && (
                  <p className="text-sm text-[var(--text-secondary)]">
                    Use o formulário ao lado para falar com a nossa equipe.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[var(--primary)]/10 to-[var(--secondary)]/10 rounded-2xl p-6">
              <h3 className="font-bold text-sm mb-2">Horário de atendimento</h3>
              <p className="text-sm text-[var(--text-secondary)]">
                Segunda a Sexta: 9h às 18h<br/>
                Sábado: 9h às 13h
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
              <h2 className="font-bold text-xl mb-6" style={{ fontFamily: 'Manrope' }}>
                Envie sua mensagem
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Nome completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
                      placeholder="Seu nome"
                      data-testid="contact-nome-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
                      placeholder="seu@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      WhatsApp
                    </label>
                    <input
                      type="text"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({ ...formData, whatsapp: formatWhatsapp(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
                      placeholder="(31) 99999-9999"
                      data-testid="contact-whatsapp-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Assunto <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.assunto}
                      onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
                      data-testid="contact-assunto-select"
                    >
                      <option value="">Selecione um assunto</option>
                      {assuntos.map(assunto => (
                        <option key={assunto} value={assunto}>{assunto}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Mensagem <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    value={formData.mensagem}
                    onChange={(e) => setFormData({ ...formData, mensagem: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all resize-none"
                    placeholder="Escreva sua mensagem aqui..."
                    data-testid="contact-mensagem-textarea"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[var(--primary)] text-white py-3.5 rounded-xl font-semibold hover:bg-[var(--primary)]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <span>Enviando...</span>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
